import React, { useMemo, useCallback, useState, useEffect } from "react";
import TableContainer from "../../components/Common/TableContainer.js";
import Breadcrumbs from "../../components/Common/Breadcrumb.js";
import { Card, CardBody, Button, Container } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { generateColumns } from "../../custom/Utils/ColumnUtils.js";
import useFetchData from "../../custom/Utils/UseFetchDataUtils.js";
import usePagination from "../../custom/Utils/UsePaginationUtils.js";
import { listPageData, exportPageData } from "./JobCardFile.js";
import { formatDate, getRecords } from "../../services/api.service.js";
import { LoaderCreatePage } from "../Loader/Loader.js";
import {
  formContentContainer,
  loaderOverlay,
} from "../../Style/loaderOverly.js";
import formatDateTime from "../../custom/Utils/formatDateTime.js";

const JobCardList = () => {
  const navigate = useNavigate();
  const [updatedData, setUpdatedData] = useState([]);
  const {
    payload,
    navigatePage,
    headerName,
    columnData,
    endpoint,
    searchfields,
    exportFileName,
  } = listPageData();
  const initialPayload = useMemo(() => payload, []);

  // pagination
  const {
    page,
    pageSize,
    nextPage,
    previousPage,
    lastPage,
    firstPage,
    handlePageSizeChange,
    setPage,
  } = usePagination();

  // fetch data using the provided payload, page, and page size
  const { data, isLoading, setSearchKeyword, totalCount } = useFetchData(
    initialPayload,
    page,
    pageSize,
    endpoint,
    searchfields,
    { onResetPage: firstPage }
  );

  const [dataForExport, setDataForExport] = useState([]);

  // handle create action
  const handleCreate = () => navigate(navigatePage.create);

  const getOnlyDate = (date) => {
    if (!date) return "-";

    const d = new Date(date);
    if (isNaN(d.getTime())) return "-";

    return d
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
      .replace(/\//g, "-");
  };

  useEffect(() => {
    if (data) {
      const newArray = data.map((item) => {
        return {
          ...item,
          technician_check_is_repairable:
            item.technician_check_is_repairable == null
              ? item.technician_check_is_repairable // keeps null/undefined
              : item.technician_check_is_repairable
                ? "Yes"
                : "No",
          technician_check_at:
            item.technician_check_at == null
              ? item.technician_check_at // keeps null/undefined
              : getOnlyDate(item.technician_check_at),
          technician_full_name: item.technician
            ? `${item.technician?.first_name} ${item.technician?.last_name}`
            : "-",
          assigned_by_full_name: item.assigned_by_user
            ? `${item.assigned_by_user?.first_name} ${item.assigned_by_user?.last_name}`
            : "-",
        };
      });
      setUpdatedData(newArray);
    }
  }, [data]);

  // handle edit action
  const handleEdit = useCallback(
    (id, status) => {
      if (status !== "Completed") navigate(navigatePage.edit + "/" + id);
    },
    [navigate, navigatePage.edit],
  );

  const handleView = useCallback(
    (id) => {
      navigate(navigatePage.view + "/" + id);
    },
    [navigate, navigatePage.view],
  );
  const handleTechnicianAction = useCallback(
    (id) => {
      navigate("/job-card-technician-repair" + "/" + id);
    },
    [navigate, "/job-card-technician-repair"],
  );

  // columns data
  const columns = useMemo(
    () => [
      {
        Header: "SR.NO",
        accessor: "index",
        accessor: (row) => page * pageSize + row.index,
        disableFilters: true,
        filterable: false,
        Cell: ({ value }) => <div style={{ width: "50px" }}>{value}</div>,
      },

      {
        Header: "JOB CARD",
        accessor: (row) => (row?.job_card_no ? row?.job_card_no : "-"),
        disableFilters: true,
        filterable: false,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "SERIAL NUMBER",
        accessor: (row) =>
          row?.intake && row?.intake ? row?.intake?.material_serial_no : "-",
        disableFilters: true,
        filterable: false,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "INTAKE NUMBER",
        accessor: (row) =>
          row?.intake && row?.intake ? row?.intake?.intake_no : "-",
        disableFilters: true,
        filterable: false,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "GMDS CODE",
        accessor: (row) =>
          row?.intake && row?.intake?.material
            ? row?.intake?.material?.gmds_code || row?.intake?.material?.name
            : "-",
        disableFilters: true,
        filterable: false,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "ENGINEER",
        accessor: (row) =>
          row?.technician_full_name ? row?.technician_full_name : "-",
        disableFilters: true,
        filterable: false,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "ASSIGNED BY",
        accessor: (row) =>
          row?.assigned_by_full_name ? row?.assigned_by_full_name : "-",
        disableFilters: true,
        filterable: false,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "CHECKED AT",
        accessor: (row) =>
          row?.technician_check_at ? row?.technician_check_at : "-",
        disableFilters: true,
        filterable: false,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "IS REPAIRABLE",
        accessor: (row) =>
          row?.technician_check_is_repairable
            ? row?.technician_check_is_repairable
            : "-",
        disableFilters: true,
        filterable: false,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      // {
      //   Header: "CHECK REMARK",
      //   accessor: (row) =>
      //     row?.technician_check_remark ? row?.technician_check_remark : "-",
      //   disableFilters: true,
      //   filterable: false,
      //   Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      // },
      {
        Header: "STATUS",
        accessor: (row) => (row?.status ? row?.status : "-"),
        disableFilters: true,
        filterable: false,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },

      {
        Header: "IS ACTIVE",
        accessor: "is_active",
        disableFilters: true,
        filterable: false,
        Cell: ({ value }) => (
          <div style={{ width: "100px" }}>
            {value === false || value === 0 ? (
              <span className="badge bg-danger-subtle text-danger text-uppercase">
                In-active
              </span>
            ) : (
              <span className="badge bg-success-subtle text-success text-uppercase">
                Active
              </span>
            )}
          </div>
        ),
      },
      {
        Header: "ACTION",
        accessor: "actions",
        disableFilters: true,
        filterable: false,
        Cell: ({ row }) => (
          <div style={{ width: "150px", display: "flex" }}>
            {/* <div
                onClick={() => {
                  handleEdit(row.original.id, row.original.status)
                }}
                title={`${row.original.status === "Completed" ? "Already Approved" : ""}`}
              >
                <Link className="me-3 text-primary" style={{ display: `${row.original.status === "Completed" ?"none" : "block"}` }}>
                  <i className="mdi mdi-pencil font-size-18"></i>
  
                </Link>
              </div> */}

            <div
              onClick={() => {
                handleView(row.original.id);
              }}
            >
              <Link className="me-3 text-primary">
                <i className="mdi mdi-eye font-size-18"></i>
              </Link>
            </div>
            {/* {console.log("status", row.original.status)} */}
            {row.original.status == "Approved" && (
              <div
                onClick={() => {
                  handleTechnicianAction(row.original.id);
                }}
              >
                <Link className="me-3 text-primary">
                  <i className="mdi mdi-wrench font-size-18"></i>
                </Link>
              </div>
            )}
          </div>
        ),
      },
    ],
    [page, pageSize],
  );

  // handle page size change callback
  const handlePageSizeChangeWrapper = useCallback(
    (newSize) => {
      handlePageSizeChange(newSize, totalCount);
    },
    [totalCount],
  );

  // useEffect(() => {
  //   const handleExportData = async () => {
  //     try {
  //       const responseData = await getRecords(exportPageData, endpoint);
  //       setDataForExport(responseData.data);
  //     } catch (error) {
  //       console.error("Error fetching records:", error);
  //     }
  //   };

  //   handleExportData();
  // }, []);

  // const exportData = dataForExport?.map((item, index) => ({
  //   "Sr. No": index + 1,
  //   "MENU NAME": item?.menu_name || "N/A",
  //   "MENU URL": item?.menu_url || "N/A",
  //   "MENU ICON": item?.menu_icon || "N/A",
  //   "CREATED BY": item?.createdBy?.first_name || "N/A",
  //   STATUS:
  //     item?.is_active === false || item?.is_active === 0
  //       ? "In-active"
  //       : "Active",
  //   "IS CHILD": item?.is_child === false || item?.is_child === 0 ? "NO" : "YES",
  //   "IS VISIBLE ON MOBILE":
  //     item?.is_visible_on_mobile === false || item?.is_visible_on_mobile === 0
  //       ? "NO"
  //       : "YES",
  // }));

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "10px",
            }}
          >
            <Breadcrumbs title={headerName + " List"} breadcrumbItems={[]} />
            {/* <Button
              color="primary"
              className="waves-effect waves-light me-1"
              onClick={handleCreate}
              style={{ marginLeft: "auto", background: "#006eb9" }}
            >
              Create {headerName}
            </Button> */}
          </div>

          <Card>
            <CardBody>
              <TableContainer
                columns={columns || []}
                data={updatedData || []}
                isLoading={isLoading}
                onSearchInputData={setSearchKeyword}
                pageIndexProp={page}
                onPageSizeChange={handlePageSizeChangeWrapper}
                nextToPage={nextPage}
                lastToPage={() => lastPage(totalCount)}
                previousToPage={previousPage}
                previousFirstToPage={firstPage}
                totalCount={totalCount}
                customPageSize={pageSize}
                // exportData={exportData}
                // exportFileName={exportFileName}
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default JobCardList;
